import { useState, useMemo, useEffect } from "react";
import { User, Folder, Timer } from "lucide-react";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Badge } from "../../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Device } from "../../interfaces/EndpointInterfaces";
import { getFolders, getFoldersForProfile, updateFolderTrustLevel, updateFolderTrustLevelforProfile } from "../Api/resourceApi";
import { myLocalStorage } from "../../../StorageHelper";
import { Folders } from "../resourceInterface";
import { Skeleton } from "../../../ui/skeleton";
import { getInheritedEndpointResourceStatusValue, getInheritedEndpointResourceStatusValueForFolders, getTimerDisplay, labelMapping } from "../helper";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import { Label } from "../../../ui/label";
import useUserStore from "../../../../services/userStore";

export interface DataAccessTabProps {
  selectedUser: string | null;
  onUserSelect: (userId: string | null) => void;
  users: any[];
  device: Device;
  profileId: number;
  os: string;
}

type Status = "Allowed" | "Denied" | "Timed" | "Inherited";

export default function DataAccessTab({
  selectedUser,
  onUserSelect,
  users,
  device,
  profileId,
  os
}: DataAccessTabProps) {
  const [selectedFolder, setSelectedFolder] = useState<Folders | null>(null);
  const [folderSearch, setFolderSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [folderStatuses, setFolderStatuses] = useState<
    Record<string, Record<string, Status>>
  >({});
  const [timerData, setTimerData] = useState<
    Record<string, { remainingMinutes: number; totalMinutes: number }>
  >({
    pictures: { remainingMinutes: 45, totalMinutes: 60 },
  });
  const [privilegeFilter, setPrivilegeFilter] = useState("ALL");

  const [folders, setFolders] = useState<Folders[]>([]);
  const [loading, setLoading] = useState(false);
  const tenantName = myLocalStorage.getItem("latestTenant").tenantName || "";
  const { email, role } = useUserStore((state) => state.user);

  const normalizeStatus = (status: any): string | undefined => {
    const normalizedStatus = String(status ?? "").toUpperCase();
    if (!normalizedStatus) return undefined;

    if (normalizedStatus === "ENABLED" || normalizedStatus === "ALLOWED") return "ENABLED";
    if (normalizedStatus === "DISABLED" || normalizedStatus === "DENIED" || normalizedStatus === "BLOCKED") return "DISABLED";
    if (normalizedStatus === "DYNAMIC" || normalizedStatus === "TIMED") return "DYNAMIC";
    if (normalizedStatus === "INHERITED") return "INHERITED";

    return normalizedStatus;
  };

  const getFolderStatus = (folder: Folders) => {
    if (selectedUser === "system") {
      const profileStatus = normalizeStatus(folder.profileResource);
      const trustStatus = normalizeStatus((folder as any)?.trustLevel);

      if (profileStatus === "INHERITED" && trustStatus) {
        return trustStatus;
      }

      return profileStatus || trustStatus;
    }

    if (!selectedUser) {
      return normalizeStatus(folder.groupStatus) || normalizeStatus(folder.profileResource);
    }

    return (
      folderStatuses[selectedUser]?.[folder.folderId] ||
      normalizeStatus(folder.groupStatus) ||
      normalizeStatus(folder.profileResource)
    );
  };

  const getStatus = (folder: Folders) => {
    const inheritedStatus = selectedUser === "system" ? "" : getInheritedEndpointResourceStatusValueForFolders(folder);
    return labelMapping(getFolderStatus(folder) || "", inheritedStatus || "")
  }

  const handleFolderStatusChange = async (folderId: number, folder: Folders, newStatus: string) => {
    if (!selectedUser) return;

    const normalizedStatus = newStatus === "BLOCKED" ? "DISABLED" : newStatus;
    const isSystemUser = selectedUser === "system";

    setFolderStatuses((prev) => ({
      ...prev,
      [selectedUser]: {
        ...(prev[selectedUser] ?? {}),
        [folderId]: normalizedStatus as Status,
      },
    }));

    const resourceMapping: Record<string, string> = {
      ENABLED: "ALLOWED",
      DISABLED: "BLOCKED",
      DYNAMIC: "DYNAMIC",
      INHERITED: "INHERITED",
    };

    // Update UI state of folders
    const updatedFolders = folders.map((f) =>
      f.folderId === folder.folderId
        ? {
          ...f,
          status: normalizedStatus,
          groupStatus: normalizedStatus,
          ...(isSystemUser
            ? {
              trustLevel: resourceMapping[normalizedStatus] || normalizedStatus,
              profileResource: resourceMapping[normalizedStatus] || "",
            }
            : {}),
        }
        : f
    );

    setFolders(updatedFolders);
    setSelectedFolder((prevSelectedFolder) => {
      if (!prevSelectedFolder || prevSelectedFolder.folderId !== folder.folderId) return prevSelectedFolder;

      return {
        ...prevSelectedFolder,
        status: normalizedStatus,
        groupStatus: normalizedStatus,
        ...(isSystemUser
          ? {
            trustLevel: resourceMapping[normalizedStatus] || normalizedStatus,
            profileResource: resourceMapping[normalizedStatus] || "",
          }
          : {}),
      };
    });

    try {
      const timeInSeconds = (device?.defaultTime || 0) * 60;

      if (selectedUser === "system")
        await updateFolderTrustLevelforProfile(profileId, folder, normalizedStatus, tenantName, email, role);
      else
        await updateFolderTrustLevel(device?.id, folder, normalizedStatus, tenantName, selectedUser, email, role, timeInSeconds);
    } catch (error) {
      console.error("Failed to update folder trust level:", error);
    }
    await fetchFolder(false);
  };

  const getFolderSelectStatusValue = (folder: Folders | null | undefined) => {
    if (!folder) return undefined;
    const status = getFolderStatus(folder);
    if (!status) return undefined;

    const normalizedStatus = normalizeStatus(status) || String(status).toUpperCase();
    if (normalizedStatus === "INHERITED" && selectedUser === "system") {
      const hasCategoryName = Boolean(String((folder as any)?.categoryName ?? "").trim());
      if (hasCategoryName) return "INHERITED";

      const inheritedStatus = normalizeStatus((folder as any)?.trustLevel || folder.profileResource || "");
      if (inheritedStatus === "DISABLED" || inheritedStatus === "DYNAMIC" || inheritedStatus === "ENABLED") return inheritedStatus;
      return "ENABLED";
    }
    return normalizedStatus;
  }

  const fetchFolder = async (isFirst = true) => {
    if (!selectedUser) return;
    setLoading(isFirst);
    try {
      let response = [];

      if (selectedUser === "system")
        response = await getFoldersForProfile(profileId, os);
      else
        response = await getFolders(
          tenantName,
          device?.id,
          selectedUser || ""
        );
      setFolders(response);
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    const interval = setInterval(() => {
      setTimerData((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((folderId) => {
          if (updated[folderId].remainingMinutes > 0) {
            updated[folderId] = {
              ...updated[folderId],
              remainingMinutes: updated[folderId].remainingMinutes - 1,
            };
          }
        });
        return updated;
      });
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const filteredUsers = useMemo(() => {
    return users.filter((user) =>
      user.cn.toLowerCase().includes(userSearch.toLowerCase())
    );
  }, [userSearch, users]);

  useEffect(() => {
    fetchFolder();
  }, [selectedUser]);



  const filteredFolders = useMemo(() => {
    const filterMap: Record<string, string[]> = {
      ALL: ["ALLOWED", "DENIED", "TIMED", "INHERITED"],
      ALLOWED: ["ALLOWED"],
      DENIED: ["DENIED"],
      TIMED: ["TIMED"],
    };

    const foldersToFilter =
      selectedUser === "system"
        ? folders.filter((folder, index, array) => {
          const normalizedPath = String(folder.path ?? "").trim().toLowerCase();
          return (
            array.findIndex(
              (item) =>
                String(item.path ?? "").trim().toLowerCase() === normalizedPath
            ) === index
          );
        })
        : folders;

    const filtered = foldersToFilter.filter((app) => {
      const status = getStatus(app);
      const matchesPrivilege = filterMap[privilegeFilter]?.includes(status) ?? true;
      const matchesSearch = app.path
        .toLowerCase()
        .includes(folderSearch.toLowerCase());
      return matchesPrivilege && matchesSearch;
    });
    return filtered;

  }, [folders, folderSearch, privilegeFilter, folderStatuses, selectedUser]);


  useEffect(() => {
    setSelectedFolder(null);
  }, [privilegeFilter])


  const getLastPathPart = (path: string) => {
    const parts = path.split("\\").filter(Boolean);
    return parts.length > 0 ? parts[parts.length - 1] : path;
  };

  const canShowInheritedOptionForSystem =
    selectedUser === "system" &&
    Boolean(String((selectedFolder as any)?.categoryName ?? "").trim());


  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Users List */}
      {selectedUser !== "system" && (
        <div className="w-64 p-4 overflow-y-auto bg-white border-r dark:bg-background">
          <h3 className="mb-4 text-sm font-medium">Users List</h3>
          <div className="mb-3">
            <Input
              placeholder="Search users..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="text-sm"
              data-testid="input-user-search"
            />
          </div>
          <div className="space-y-3">
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${String(selectedUser) === String(user.id)
                    ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                    : "hover-elevate text-muted-foreground"
                    }`}
                  onClick={() => {
                    onUserSelect(user.id);
                    setSelectedFolder(null);
                    setFolderSearch("");
                  }}
                  data-testid={`user-${user.id}`}
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted">
                    <User className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">{user.cn}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-4 text-sm text-center text-muted-foreground">
                No users found
              </div>
            )}
          </div>
        </div>
      )}

      {/* Folders List */}
      <div className="flex flex-col flex-1 p-4 overflow-hidden bg-white border-r dark:bg-background">
        <h3 className="mb-4 text-sm font-medium">
          Folders ({filteredFolders.length || 0})
        </h3>
        <div className="mb-3">
          <Input
            placeholder="Search folders..."
            value={folderSearch}
            onChange={(e) => setFolderSearch(e.target.value)}
            className="text-sm"
            disabled={!selectedUser && selectedUser !== "system"}
            data-testid="input-folder-search"
          />
        </div>
        <div className="flex items-center gap-4 mb-2">
          <span className="text-sm font-medium text-muted-foreground">Privilege</span>
          <RadioGroup value={privilegeFilter} onValueChange={setPrivilegeFilter} className="flex gap-4">
            {["ALL", "ALLOWED", "DENIED", "TIMED"].map((v) => (
              <div key={v} className="flex items-center space-x-2">
                <RadioGroupItem value={v} id={`filter-${v.toLowerCase()}`} />
                <Label htmlFor={`filter-${v.toLowerCase()}`} className="text-sm">{v}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
        <div className="flex-1 pr-1 space-y-2 overflow-y-auto">
          {loading ? (
            Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} className="p-3 border rounded-md">
                <Skeleton className="w-3/4 h-4 mb-2" />
                <Skeleton className="w-1/2 h-3" />
              </div>
            ))
          ) : selectedUser ? (
            filteredFolders.length > 0 ? (
              filteredFolders
                .map((folder) => (
                  <div
                    key={folder.folderId}
                    className={`p-3 rounded-md cursor-pointer transition-colors border ${selectedFolder?.folderId === folder.folderId
                      ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700"
                      : "hover-elevate border-border"
                      }`}
                    onClick={() => setSelectedFolder(folder)}
                    data-testid={`folder-${folder.folderId}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center flex-1 min-w-0 gap-3">
                        <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded bg-muted">
                          <Folder className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">

                            {getLastPathPart(folder.path.split("/").pop() || folder.path)}
                          </div>
                          <div className="font-mono text-xs break-all text-muted-foreground">
                            {folder.path}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center flex-shrink-0 gap-2">
                        <Badge
                          variant={
                            getStatus(folder) === "ALLOWED"
                              ? "default"
                              : getStatus(folder) === "DENIED"
                                ? "destructive"
                                : getStatus(folder) === "TIMED"
                                  ? "secondary"
                                  : "default"
                          }
                          className="text-xs min-w-[60px] text-center"
                        >
                          {getStatus(folder)}
                        </Badge>
                        {getStatus(folder) === "TIMED" && selectedUser !== "system" &&

                          <div className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 font-medium min-w-[50px]">
                            <Timer className="w-3 h-3" />
                            <span>{getTimerDisplay(folder.remainingTime)}</span>
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                ))
            ) : (
              <div className="py-8 text-sm text-center text-muted-foreground">
                No folders available
              </div>
            )
          ) : (
            <div className="py-8 text-sm text-center text-muted-foreground">
              Select a user to view folders
            </div>
          )}
        </div>
      </div>

      <div className="p-4 overflow-y-auto bg-white border-l w-80 dark:bg-background">
        {selectedFolder ? (
          <div className="space-y-6">
            <div>
              <h3 className="mb-2 text-lg font-semibold">
                {getLastPathPart(selectedFolder.path.split("/").pop() || selectedFolder.path)}
              </h3>
              <p className="font-mono text-xs break-all text-muted-foreground">
                {selectedFolder.path}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block mb-2 text-xs font-medium tracking-wide uppercase text-muted-foreground">
                  Access Control
                </label>
                <Select
                  value={getFolderSelectStatusValue(selectedFolder)}
                  onValueChange={(value) =>
                    handleFolderStatusChange(selectedFolder.folderId, selectedFolder, value)
                  }
                >
                  <SelectTrigger className="h-auto min-h-8 text-xs w-55 border-blue-200 whitespace-normal break-words dark:border-blue-700 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 font-medium hover:bg-blue-100 dark:hover:bg-blue-900/40 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">

                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      value="ENABLED"
                      data-testid="option-status-allowed"
                    >
                      ALLOWED
                    </SelectItem>
                    <SelectItem
                      value="DISABLED"
                      data-testid="option-status-denied"
                    >
                      DENIED
                    </SelectItem>
                    <SelectItem
                      value="DYNAMIC"
                      data-testid="option-status-timed"
                    >
                      TIMED
                    </SelectItem>
                    {(selectedUser !== "system" || canShowInheritedOptionForSystem) && (
                      <SelectItem
                        value="INHERITED"
                        data-testid="option-status-inherited"
                      >
                        INHERITED {getInheritedEndpointResourceStatusValueForFolders(selectedFolder)}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="text-sm text-muted-foreground">
                {selectedFolder.path === "Documents" &&
                  "User documents and files folder"}
                {selectedFolder.path === "Downloads" &&
                  "Downloaded files from internet"}
                {selectedFolder.path === "Desktop" &&
                  "Desktop files and shortcuts"}
                {selectedFolder.path === "Pictures" && "User images and photos"}
                {selectedFolder.path === "Videos" && "User video files"}
                {selectedFolder.path === "Music" && "User audio and music files"}
                {selectedFolder.path === "Temp" && "Temporary system files"}
                {selectedFolder.path === "Program Files" &&
                  "Installed applications and programs"}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-sm text-center text-muted-foreground">
              <div className="mb-2">📁</div>
              <div>No folder selected</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
